import { z } from "zod";
import { zWorldSeed, type WorldRecord } from "./worlds";
import { zPerformanceBundle } from "./performances";
import { zShowAppearance } from "./showCast";

/** Published gifts are independent, deliberately public-account-owned copies. */
export const WORLD_RELEASE_COLLECTION_PATH = "Account/public/WorldRelease";
export const WORLD_RELEASE_ASSET_PREFIX = "Account/public/WorldReleases/";

const id = z.string().regex(/^[a-zA-Z0-9_-]{1,100}$/);
export const zWorldSeedSelection = z
  .object({
    entityIds: z.array(id).min(1).max(2000),
    factKeys: z.array(z.string().max(300)).max(10000).optional(),
    threadIds: z.array(id).max(1000).optional(),
  })
  .strict();
export const zWorldReleasePolicy = z
  .object({
    publisherId: id.optional(),
    programId: id.optional(),
    permissions: zWorldSeed.shape.permissions,
    attribution: z.string().max(1000),
    personalization: z.enum(["independent", "program-only"]).default("independent"),
    derivativeOf: z.array(id).max(100).default([]),
  })
  .strip();
export type WorldReleasePolicy = z.infer<typeof zWorldReleasePolicy>;
export const zWorldReleasePayload = z
  .object({
    schemaVersion: z.literal(3),
    seed: zWorldSeed,
    policy: zWorldReleasePolicy,
    story: zPerformanceBundle.optional(),
    appearances: z.record(zShowAppearance).default({}),
    entityBindings: z.record(id).default({}),
    /** Opaque release-local character identity; artwork never establishes identity. */
    characterBindings: z.record(id).optional(),
  })
  .strip();
export type WorldReleasePayload = z.infer<typeof zWorldReleasePayload>;
export const zWorldRelease = z
  .object({
    schemaVersion: z.literal(3),
    id,
    accountId: z.literal("public").default("public"),
    name: z.string().max(200),
    enabled: z.boolean(),
    snapshotId: id,
    chunkIds: z.array(id).max(500),
    complete: z.literal(true),
    seed: zWorldSeed.pick({ name: true, permissions: true, attribution: true }),
    policy: zWorldReleasePolicy,
    createdAt: z.number().int().nonnegative(),
    updatedAt: z.number().int().nonnegative().optional(),
    /** Reviewed identity supplement for older frozen payloads. */
    characterBindings: z.record(id).optional(),
  })
  .strip();
export type WorldRelease = z.infer<typeof zWorldRelease>;
const source = { ownerAccountId: id, worldId: id };
const selection = { selection: zWorldSeedSelection, allowMemberMaterial: z.boolean().default(false) };
const publication = {
  ...source,
  ...selection,
  requestId: id,
  expectedRevision: z.number().int().nonnegative(),
  expectedProfileRevision: z.number().int().nonnegative(),
  expectedHistoryId: id,
  name: z.string().trim().min(1).max(200),
  permissions: zWorldSeed.shape.permissions,
  attribution: z.string().trim().max(1000),
  branchId: id.optional(),
};
export const zManageWorldSeedRequest = z.discriminatedUnion("action", [
  z.object({ action: z.literal("previewSeed"), ...source, ...selection }).strict(),
  z
    .object({
      action: z.literal("releaseSeed"),
      ...publication,
      storyId: id.optional(),
    })
    .strict(),
  z
    .object({
      action: z.literal("updateRelease"),
      ...publication,
      storyId: id,
      releaseId: id,
      expectedSnapshotId: id,
    })
    .strict(),
  z.object({ action: z.literal("revokeRelease"), ...source, releaseId: id }).strict(),
  z
    .object({
      action: z.literal("installRelease"),
      releaseId: id,
      destinationAccountId: id,
      destinationWorldId: id.optional(),
      adoptStyle: z.boolean().default(false),
    })
    .strict(),
  z.object({ action: z.literal("startWorldGuest"), releaseId: id }).strict(),
  z
    .object({
      action: z.literal("claimWorldGuest"),
      guestId: id,
      destinationAccountId: id,
      claimToken: z.string().min(32).max(200).optional(),
    })
    .strict(),
]);
export type ManageWorldSeedRequest = z.input<typeof zManageWorldSeedRequest>;
export type ParsedManageWorldSeedRequest = z.infer<typeof zManageWorldSeedRequest>;
export interface WorldSeedClosurePreview {
  requestedEntityIds: string[];
  requiredEntityIds: string[];
  privateEntityIds: string[];
  missingEntityIds: string[];
  requiredFactKeys: string[];
  privateFactKeys: string[];
  requiredThreadIds: string[];
  requiredAssetIds: string[];
  ready: boolean;
  revision: number;
  profileRevision: number;
  expectedHistoryId: string;
  policy: WorldReleasePolicy;
}
export interface ManageWorldSeedResponse {
  preview?: WorldSeedClosurePreview;
  release?: WorldRelease;
  releaseId?: string;
  world?: WorldRecord;
  worldId?: string;
  accountId?: string;
  storyId?: string;
  guestId?: string;
  claimToken?: string;
  duplicate?: boolean;
}
